import { DynamoDBClient } from "@aws-sdk/client-dynamodb";
import { DeleteObjectCommand, S3Client } from "@aws-sdk/client-s3";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-providers";
import {
  DeleteCommand,
  DynamoDBDocumentClient,
  GetCommand,
  PutCommand,
  ScanCommand,
  UpdateCommand,
  BatchWriteCommand,
} from "@aws-sdk/lib-dynamodb";
import { Upload } from "@aws-sdk/lib-storage";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { setDocClient, setS3Client } from "redux/reducers/awsClient/awsClient";

export const useAWS = () => {
  const dispatch = useAppDispatch();
  const docClient: DynamoDBDocumentClient | null = useAppSelector(
    (state) => state.awsClient.docClient
  );
  const s3Client: S3Client | null = useAppSelector(
    (state) => state.awsClient.s3Client
  );

  const createDocClient = (): DynamoDBDocumentClient => {
    const client = new DynamoDBClient({
      region: process.env.REACT_APP_CREDENTAL_REGION || "",
      credentials: fromCognitoIdentityPool({
        clientConfig: { region: process.env.REACT_APP_CREDENTAL_REGION || "" },
        identityPoolId: process.env.REACT_APP_CREDENTAL_ID || "",
      }),
    });
    return DynamoDBDocumentClient.from(client);
  };

  const getDocClient = (): DynamoDBDocumentClient => {
    if (docClient) {
      return docClient;
    }
    const client = createDocClient();
    dispatch(setDocClient(client));
    return client;
  };

  const createS3Client = (): S3Client => {
    return new S3Client({
      region: process.env.REACT_APP_CREDENTAL_REGION || "",
      credentials: fromCognitoIdentityPool({
        clientConfig: { region: process.env.REACT_APP_CREDENTAL_REGION || "" },
        identityPoolId: process.env.REACT_APP_CREDENTAL_ID || "",
      }),
    });
  };

  const getS3Client = (): S3Client => {
    if (s3Client) {
      return s3Client;
    }
    const client = createS3Client();
    dispatch(setS3Client(client));
    return client;
  };

  const docScan = (tableName: string): Promise<any> => {
    const client = getDocClient();
    const scanCommand = new ScanCommand({
      TableName: tableName,
      ConsistentRead: true,
    });
    return client.send(scanCommand);
  };

  const docGet = async (
    tableName: string,
    key: { [key: string]: any }
  ): Promise<any> => {
    const client = getDocClient();
    const getCommand = new GetCommand({
      TableName: tableName,
      Key: key,
      ConsistentRead: true,
    });
    return client.send(getCommand).then((res) => {
      return res.Item;
    });
  };

  const docPut = (tableName: string, item: any): Promise<any> => {
    const client = getDocClient();
    const command = new PutCommand({
      TableName: tableName,
      Item: item,
    });

    return client.send(command);
  };

  const docUpdate = (
    tableName: string,
    id: string,
    update: { [key: string]: any }
  ) => {
    const client = getDocClient();

    const attributes = Object.keys(update);
    let updateExpression = "set ";
    attributes.forEach((attr: string, index: number) => {
      if (index === 0) {
        updateExpression += `#${attr} = :${attr}`;
        return;
      }
      updateExpression += `, #${attr} = :${attr}`;
    });
    const expressionAttributeNames: { [key: string]: any } = {};
    const expressionAttributeValues: { [key: string]: any } = {};
    attributes.forEach((attr: string) => {
      expressionAttributeNames[`#${attr}`] = attr;
      expressionAttributeValues[`:${attr}`] = update[attr];
    });
    const command = new UpdateCommand({
      TableName: tableName,
      Key: { id },
      UpdateExpression: updateExpression,
      ExpressionAttributeNames: expressionAttributeNames,
      ExpressionAttributeValues: expressionAttributeValues,
      ReturnValues: "ALL_NEW",
    });
    return client.send(command);
  };

  const docDelete = (tableName: string, id: string) => {
    const client = getDocClient();
    const command = new DeleteCommand({
      TableName: tableName,
      Key: { id },
    });
    return client.send(command);
  };

  const docBatchWrite = async (
    tableName: string,
    items: any[],
    maxRetries: number = 3
  ): Promise<any> => {
    const client = getDocClient();
    const batchSize = 25; // DynamoDB batch write limit
    const batches = [];

    for (let i = 0; i < items.length; i += batchSize) {
      const batch = items.slice(i, i + batchSize);
      const requestItems = {
        [tableName]: batch.map((item) => ({
          PutRequest: {
            Item: item,
          },
        })),
      };
      batches.push(requestItems);
    }

    const processBatch = async (
      batch: any,
      retryCount: number = 0
    ): Promise<any> => {
      try {
        const command = new BatchWriteCommand({
          RequestItems: batch,
        });
        const response = await client.send(command);

        // Check for unprocessed items
        if (
          response.UnprocessedItems &&
          Object.keys(response.UnprocessedItems).length > 0
        ) {
          if (retryCount < maxRetries) {
            // Exponential backoff: wait 2^retryCount * 100ms
            const delay = Math.pow(2, retryCount) * 100;
            await new Promise((resolve) => setTimeout(resolve, delay));
            return processBatch(response.UnprocessedItems, retryCount + 1);
          } else {
            throw new Error(
              `Failed to process items after ${maxRetries} retries`
            );
          }
        }
        return response;
      } catch (error) {
        if (retryCount < maxRetries) {
          const delay = Math.pow(2, retryCount) * 100;
          await new Promise((resolve) => setTimeout(resolve, delay));
          return processBatch(batch, retryCount + 1);
        }
        throw error;
      }
    };

    return Promise.all(batches.map((batch) => processBatch(batch)));
  };

  const s3UploadFile = async (file: File, key: string): Promise<string> => {
    try {
      const client = getS3Client();
      const parallelUploads3 = new Upload({
        client: client,
        params: {
          Bucket: process.env.REACT_APP_S3_BUCKET_NAME,
          Key: key,
          Body: file,
          ACL: "public-read",
          ContentType: file.type || "application/octet-stream",
          CacheControl: "max-age=31536000",
        },
        queueSize: 1,
        partSize: 1024 * 1024 * 1024, // 1GB
      });

      parallelUploads3.on("httpUploadProgress", (progress) => {
        //console.log("Upload progress:", progress);
      });

      //const result =
      await parallelUploads3.done();
      //console.log("Upload result:", result);

      const objectUrl = `${process.env.REACT_APP_S3_BUCKET_URL}/${key}`;
      return objectUrl;
    } catch (error) {
      console.error("S3 upload error:", error);
      throw error;
    }
  };

  const s3DeleteFile = (fileUrl: string): Promise<any> => {
    const urlMatched = fileUrl.split(`${process.env.REACT_APP_S3_BUCKET_URL}/`);
    if (!urlMatched) {
      return new Promise(() => {});
    }
    const key = urlMatched[1];
    const client = getS3Client();
    const command = new DeleteObjectCommand({
      Bucket: process.env.REACT_APP_S3_BUCKET_NAME,
      Key: key,
    });
    return client.send(command);
  };

  return {
    docScan,
    docGet,
    docPut,
    docUpdate,
    docDelete,
    docBatchWrite,
    s3UploadFile,
    s3DeleteFile,
  };
};
